import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import i18next from 'i18next'
import { ErrorMessage } from '@hookform/error-message'
import { ConfirmModal, FormInput } from '@microservices/wiskey-react-components'
import { Box, Tab, Tabs, Typography } from '@mui/material'
import { CellContext } from '@tanstack/react-table'

import { BindTypeInputs } from '@components/BindTypeInputs'
import { CommandPickerController } from '@components/hookFormControllers/CommandPickerController'
import { SwitchTable } from '@components/SwitchTable'
import { TabPanel } from '@components/Tabs'

import { useFetchObjectByCodeNoCacheMutation } from '@redux/api'

import { useDebounce, usePrevious } from '@hooks'
import { getParamsStringSafe } from '@helpers'
import {
  ENTITY_COMMAND_TYPE,
  GANTT_BIND_TYPE,
  GENERATOR_INPUT_TYPE,
  MODEL_TYPE,
  TYPES_SOURCE_OPTIONS,
} from '@constants'
import {
  AutocompleteOption,
  ModalType,
  ObjectCommandDTO,
  ObjectShortDTO,
  ShareActionType,
} from '@types'

import {
  GanttPreFilterHelper,
  GanttPreFilterVariableHelper,
} from '@gantt/components/GanttCreateOrEdit/components/GanttConfiguration/components'
import { FILTER_DEBOUNCE_MS } from '@gantt/components/GanttCreateOrEdit/components/GanttConfiguration/GanttConfiguration'
import {
  AvailableVariables,
  AXIS_TYPE,
  FILTER_BINDING_TYPE,
  GANTT_BIND_TYPE_LIST,
  GanttActionType,
  GanttColumnForm,
} from '@gantt/components/GanttCreateOrEdit/types'
import {
  COMMON_META_DATA,
  DefReadAll,
  GANTT_CONFIG_ACTION_COLUMNS,
  GANTT_CONFIG_COLUMNS,
  GANTT_RESOURCE_COLUMN_ROW_HEIGHT,
  JS_FUN_ARGS,
} from '@gantt/constants'
import {
  AutocompleteOptionsObject,
  getAutoCompleteOptions,
  getBindTypeOptionsByEnum,
  getDefaultCommand,
  getInvolvedPaths,
  getIsErrorTab,
  keyFieldOptionsFilter,
} from '@gantt/helpers'

import { getAvailableVariablesObject } from '../../helpers'
import { ResourceAdditionalFields } from '../ResourceAdditionalFields'

type Props = {
  isDisabled: boolean
  isShowDialog: boolean
  isLoadingObjects: boolean
  currentTabResource: number
  rowsColumnTable: GanttColumnForm[]
  rowsActionTable: GanttActionType[]
  objects: ObjectShortDTO[] | undefined
  filteredObjects: ObjectShortDTO[] | undefined
  onTabChangeResource: (event: SyntheticEvent, newValue: number) => void
  onDeleteColumn: (id: string | number) => void
  onEdit: (
    row: GanttColumnForm | GanttActionType | null,
    id?: number | string,
    isAction?: boolean,
    barIndex?: number,
    selectedBarObject?: AutocompleteOption<string> | null,
    inputAvailableVariables?: AvailableVariables,
    existingPaths?: string[],
    selectedPaths?: string[]
  ) => void
  onHide: (row: GanttColumnForm, checked: boolean) => void
  onOpenDialog: (props: {
    type: ModalType
    id?: number | string
    row?: GanttColumnForm | GanttActionType | null
    barIndex?: number | null
    selectedBarObject?: AutocompleteOption<string> | null
    inputAvailableVariables?: AvailableVariables
    existingPaths?: string[]
    selectedPaths?: string[]
  }) => void
  onSetActionToDeleteResource: (id: string | number) => void
  usedObjectsList: string[]
}

const filterHints = {
  static: i18next.t('ganttCreate.resourceForm.hint.filterStaticHint'),
}

const timeEdgesFilterHint = {
  static: i18next.t('ganttCreate.resourceForm.hint.timeEdgesFilter'),
}

export const ResourceForm = ({
  isShowDialog,
  isLoadingObjects,
  currentTabResource,
  rowsActionTable,
  rowsColumnTable,
  filteredObjects,
  onTabChangeResource,
  onDeleteColumn,
  onSetActionToDeleteResource,
  onOpenDialog,
  onEdit,
  onHide,
  usedObjectsList,
  objects,
}: Props) => {
  const [isShowClearModal, setIsShowClearModal] = useState(false)

  const {
    watch,
    setValue,
    resetField,
    formState: { errors },
    trigger,
  } = useFormContext()
  const { t } = useTranslation()
  const { code } = useParams()
  const watchObjectResource: { id: string; label: string } = watch('resource.data')
  const watchedMainObjectCode = watch('sort.code')
  const watchedResourceConfig = watch('resource')
  const watchedFilter = watch('resource.filter')
  const watchedTimeEdgesFilter = watch('resource.timeEdgesFilter')
  const debouncedFilter = useDebounce(getParamsStringSafe(watchedFilter), FILTER_DEBOUNCE_MS)
  const debouncedTimeEdgesFilter = useDebounce(
    getParamsStringSafe(watchedTimeEdgesFilter),
    FILTER_DEBOUNCE_MS
  )
  const watchedMainObjectAxis = watch('sort.axis')
  const watchedResourceCommands = watch('resource.commands')

  const [prevObjectId, clearId] = usePrevious(watchObjectResource?.id)
  const [prevObjectLabel, clearLabel] = usePrevious(watchObjectResource?.label)
  const [prevCommands, clearCommands] = usePrevious(watchedResourceCommands)

  const isEdit = !!code

  const [fetchObjectByCode, { data: objectByCode }] = useFetchObjectByCodeNoCacheMutation({
    fixedCacheKey: 'resource-object',
  })

  const selectedColumnsPaths = useMemo(() => {
    const result: string[] = []

    rowsColumnTable.forEach(column => {
      column.fields.forEach(field => {
        if (field?.bindType === GANTT_BIND_TYPE_LIST.FIELD && field.field?.pathStr) {
          result.push(field.field?.pathStr)
        }
      })
    })

    return result
  }, [rowsColumnTable])

  useEffect(() => {
    watchObjectResource?.id && fetchObjectByCode(watchObjectResource?.id)
  }, [watchObjectResource?.id])

  const availableVariablesObject: AvailableVariables = useMemo(() => {
    const variablesObject = Object.keys(getAvailableVariablesObject(watch(`resource`)))

    return { srcObj: variablesObject, ...COMMON_META_DATA, [JS_FUN_ARGS.restData]: [] }
  }, [watch()])

  const handleEditAction = useCallback(
    (params: CellContext<GanttActionType, unknown>) => {
      onEdit(
        params.row.original,
        params.row.original.id,
        true,
        undefined,
        null,
        availableVariablesObject,
        getInvolvedPaths(watchedResourceConfig)
      )
    },
    [onEdit]
  )
  const handleEditColumn = useCallback(
    (params: CellContext<GanttColumnForm, unknown>) => {
      onEdit(
        params.row.original,
        params.row.original.id,
        false,
        undefined,
        null,
        undefined,
        getInvolvedPaths(watchedResourceConfig),
        selectedColumnsPaths
      )
    },
    [onEdit]
  )

  const handleHideColumn = useCallback(
    (params: CellContext<GanttColumnForm, unknown>, checked: boolean) => {
      onHide(params.row.original, checked)
    },
    [onHide]
  )

  const handleClearOnChangeObject = () => {
    watchObjectResource?.id &&
      fetchObjectByCode(watchObjectResource?.id)
        .unwrap()
        .then(data => {
          const idField = data?.fields?.find(field => field.isPk)
          const internalIdField = data?.fields?.find(field => field.isInternalId)
          const key = idField || internalIdField

          const newKey = key && {
            field: key.name,
            pathStr: key.name,
            pathArray: [
              {
                objectCode: watchObjectResource?.id,
                field: key.name,
                valueType: key.valueType,
              },
            ],
          }

          const defaultCommand = getDefaultCommand(data?.commands || [], DefReadAll)

          const newDefaultCommand = defaultCommand && [
            {
              name: { id: defaultCommand.code, label: defaultCommand.code },
              type: {
                id: ENTITY_COMMAND_TYPE.GET_ALL,
                label: ENTITY_COMMAND_TYPE.GET_ALL,
              },
              sourceType: {
                id: TYPES_SOURCE_OPTIONS.OBJECT,
                label: TYPES_SOURCE_OPTIONS.OBJECT,
              },
              sourceCommand: watchObjectResource,
              httpMethod: defaultCommand.httpMethod,
              isCommandResponse: false,
              parameters: [],
              body: [],
            },
          ]

          resetField('resource', {
            defaultValue: {
              data: watchObjectResource,
              columns: [],
              actions: [],
              filter: {
                static: '',
                bindType: FILTER_BINDING_TYPE.STATIC,
              },
              timeEdgesFilter: {
                static: '',
                bindType: FILTER_BINDING_TYPE.STATIC,
              },
              key: {
                bindType: GANTT_BIND_TYPE.FIELD,
                field: newKey || { field: '', pathStr: '', pathArray: [] },
              },
              commands: newDefaultCommand ?? [{ name: null, type: null }],
            },
          })

          const watchedTimeline = watch('timeline')
          const clearedTimeline = watchedTimeline?.map((timeline: any) => ({
            ...timeline,
            sections: [],
          }))

          resetField('timeline', {
            defaultValue: clearedTimeline,
          })
        })
    handleClose(false)
  }

  const handleOpen = () => {
    setIsShowClearModal(true)
  }

  const handleClose = (resetObject: boolean) => {
    clearId?.()
    clearLabel?.()
    clearCommands?.()
    resetObject &&
      resetField('resource.data', { defaultValue: { id: prevObjectId, label: prevObjectLabel } })
    resetObject && resetField('resource.commands', { defaultValue: prevCommands })
    setIsShowClearModal(false)
  }

  const existingPaths = useMemo(() => {
    return getInvolvedPaths(watchedResourceConfig)
  }, [watchedResourceConfig])

  const handleOpenDialogColumn = (
    type: ModalType,
    id?: string | number | undefined,
    isAction?: boolean | undefined
  ) => {
    onOpenDialog({ type, id, existingPaths, selectedPaths: selectedColumnsPaths })
  }

  const handleOpenDialogAction = (
    type: ModalType,
    id?: string | number | undefined,
    isAction?: boolean | undefined
  ) => {
    onOpenDialog({
      type,
      id,
      inputAvailableVariables: availableVariablesObject,
      existingPaths,
    })
  }

  useEffect(() => {
    if (
      (watchObjectResource?.id && prevObjectId !== watchObjectResource?.id && prevObjectId) ||
      (!watchObjectResource?.id && prevObjectId)
    ) {
      handleOpen()
    }
  }, [watchObjectResource])

  useEffect(() => {
    if (!watchedTimeEdgesFilter?.bindType) {
      setValue('resource.timeEdgesFilter.bindType', FILTER_BINDING_TYPE.STATIC)
    }
  }, [watchedTimeEdgesFilter])

  const handleChangeObject = (value: AutocompleteOption<string | number>) => {
    // Если изменяем уже заполненное значение, показываем модалку подтверждения
    if (value?.id && prevObjectId !== value?.id && prevObjectId) {
      handleOpen()

      return
    }

    if (!watchedMainObjectCode) {
      setValue('sort.code', value?.id)
      setValue('sort.axis', AXIS_TYPE.Y)
    }

    // Если это первоначальное заполнение (prevObjectId пустой), сразу заполняем команды
    if (!prevObjectId && value?.id) {
      fetchObjectByCode(String(value?.id))
        .unwrap()
        .then(data => {
          const idField = data?.fields?.find(field => field.isPk)
          const internalIdField = data?.fields?.find(field => field.isInternalId)
          const key = idField || internalIdField

          const newKey = key && {
            field: key.name,
            pathStr: key.name,
            pathArray: [
              {
                objectCode: value?.id,
                field: key.name,
                valueType: key.valueType,
              },
            ],
          }
          const defaultCommand: ObjectCommandDTO | null = getDefaultCommand(
            data?.commands || [],
            DefReadAll
          )

          const newDefaultCommand = defaultCommand && [
            {
              name: { id: defaultCommand.code, label: defaultCommand.code },
              type: {
                id: ENTITY_COMMAND_TYPE.GET_ALL,
                label: ENTITY_COMMAND_TYPE.GET_ALL,
              },
              sourceType: {
                id: TYPES_SOURCE_OPTIONS.OBJECT,
                label: TYPES_SOURCE_OPTIONS.OBJECT,
              },
              sourceCommand: value,
              httpMethod: defaultCommand.httpMethod,
              isCommandResponse: false,
              parameters: [],
              body: [],
            },
          ]

          newKey && setValue('resource.key.field', newKey, { shouldValidate: true })
          newDefaultCommand &&
            setValue('resource.commands', newDefaultCommand, {
              shouldValidate: true,
            })
        })
        .finally(() => {
          trigger('resource.commands')
        })
    }
  }

  return (
    <Box key={watchObjectResource?.id} p={2} pt={0}>
      <Box borderBottom={1} borderColor={'divider'}>
        <Tabs value={currentTabResource} onChange={onTabChangeResource}>
          <Tab
            label={
              <Typography
                variant={'body2'}
                color={getIsErrorTab(errors, [
                  'resource.data',
                  'resource.key',
                  'resource.commands',
                ])}
              >
                {t(`ganttCreate.resourceForm.tabs.data`)}
              </Typography>
            }
          />
          <Tab
            label={
              <Typography color={getIsErrorTab(errors, 'resource.columns')} variant={'body2'}>
                {t(`ganttCreate.resourceForm.tabs.columns`)}
              </Typography>
            }
          />
          <Tab
            label={
              <Typography color={getIsErrorTab(errors, 'resource.actions')} variant={'body2'}>
                {t(`ganttCreate.resourceForm.tabs.actions`)}
              </Typography>
            }
          />
          <Tab
            label={
              <Typography color={getIsErrorTab(errors, 'resource.actions')} variant={'body2'}>
                {t(`ganttCreate.resourceForm.tabs.variables`)}
              </Typography>
            }
          />
          <Tab
            label={
              <Typography color={getIsErrorTab(errors, 'resource.filter')} variant={'body2'}>
                {t(`ganttCreate.resourceForm.tabs.preFilter`)}
              </Typography>
            }
          />
          <Tab
            label={
              <Typography
                color={getIsErrorTab(errors, 'resource.timeEdgesFilter')}
                variant={'body2'}
              >
                {t(`ganttCreate.resourceForm.tabs.timeEdgesFilter`)}
              </Typography>
            }
          />
        </Tabs>
      </Box>
      <TabPanel index={0} value={currentTabResource}>
        <Box pt={2}>
          <FormInput
            autocompleteOptions={getAutoCompleteOptions(objects)}
            disabled={isEdit}
            inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
            label={t('ganttCreate.resourceAxis')}
            loading={isLoadingObjects}
            name='resource.data'
            placeholder={i18next.t('placeholder.selectObject')}
            renderAutocompleteOption={(
              props: React.HTMLAttributes<HTMLLIElement>,
              option: { id: string | number; label: string }
            ) => (
              <AutocompleteOptionsObject
                list={usedObjectsList}
                option={option}
                otherProps={props}
              />
            )}
            rules={{
              required: true,
            }}
            onChangeAutocomplete={handleChangeObject}
          />
          {watchObjectResource?.id && (
            <BindTypeInputs
              bindTypeOptions={[]}
              containerName='resource.key'
              isDisabled={!watchObjectResource}
              object={watchObjectResource}
              optionsFilter={keyFieldOptionsFilter}
              valueInputLabel={t('ganttCreate.resourceForm.key')}
            />
          )}
          {watchObjectResource?.id && (
            <Box component='fieldset'>
              <legend>{t('label.commandTitle')}</legend>
              <CommandPickerController
                isAnotherObjectWithRequestData
                isSingle
                commandName='name'
                name='resource.commands'
                objectCode={watchObjectResource?.id}
                showClearButton={false}
              />
            </Box>
          )}
        </Box>
      </TabPanel>
      <TabPanel index={1} value={currentTabResource}>
        <SwitchTable
          disablePagination
          disableSort
          isCrud
          showVisibilityColumn
          btnDisabled={!watchObjectResource?.id}
          btnText={t(`ganttCreate.resourceForm.addColumn`)}
          columns={GANTT_CONFIG_COLUMNS}
          loading={false}
          rowHeight={GANTT_RESOURCE_COLUMN_ROW_HEIGHT}
          rows={rowsColumnTable}
          showActionsColumn={true}
          showDialog={isShowDialog}
          type={'gantt' as ShareActionType}
          onDelete={onDeleteColumn}
          onEdit={handleEditColumn}
          onHide={handleHideColumn}
          onShowDialog={handleOpenDialogColumn}
        />
        <ErrorMessage
          errors={errors}
          name='resource.columns'
          render={({ message }) => (
            <Typography color={'error'} pt={1}>
              {message}
            </Typography>
          )}
        />
      </TabPanel>
      <TabPanel index={2} value={currentTabResource}>
        <SwitchTable
          disablePagination
          isCrud
          showActionsColumn
          btnDisabled={!watchObjectResource?.id}
          btnText={t(`ganttCreate.resourceForm.addAction`)}
          columns={GANTT_CONFIG_ACTION_COLUMNS}
          loading={false}
          rows={rowsActionTable}
          showDialog={isShowDialog}
          type={MODEL_TYPE.GANTT_ACTION_RESOURCE}
          onDelete={onSetActionToDeleteResource}
          onEdit={handleEditAction}
          onShowDialog={handleOpenDialogAction}
        />
      </TabPanel>
      <TabPanel index={3} value={currentTabResource}>
        <ResourceAdditionalFields />
      </TabPanel>
      <TabPanel index={4} value={currentTabResource}>
        <BindTypeInputs
          isFieldsetMarkup
          bindTypeOptions={getBindTypeOptionsByEnum(FILTER_BINDING_TYPE)}
          containerName='resource.filter'
          fieldsetTitle={t('ganttCreate.resourceForm.filter')}
          hintDict={filterHints}
          inputType={GENERATOR_INPUT_TYPE.TEXTAREA}
        />
        <GanttPreFilterHelper
          appearsIn={AXIS_TYPE.Y}
          axis={watchedMainObjectAxis}
          mainObject={watchedMainObjectCode}
          object={watchObjectResource}
          preFilterValue={getParamsStringSafe(watchedFilter)}
        />
        <GanttPreFilterVariableHelper
          formPath='resource.additionalFields'
          objectCode={watchObjectResource?.id}
          rsqlSources={[debouncedFilter]}
        />
      </TabPanel>
      <TabPanel index={5} value={currentTabResource}>
        <BindTypeInputs
          isFieldsetMarkup
          bindTypeOptions={getBindTypeOptionsByEnum(FILTER_BINDING_TYPE)}
          containerName='resource.timeEdgesFilter'
          fieldsetTitle={t('ganttCreate.resourceForm.timeEdgesFilter')}
          hintDict={timeEdgesFilterHint}
          inputType={GENERATOR_INPUT_TYPE.TEXTAREA}
        />
        <GanttPreFilterHelper
          appearsIn={AXIS_TYPE.Y}
          axis={watchedMainObjectAxis}
          mainObject={watchedMainObjectCode}
          object={watchObjectResource}
          preFilterValue={getParamsStringSafe(watchedTimeEdgesFilter)}
        />
        <GanttPreFilterVariableHelper
          formPath='resource.additionalFields'
          objectCode={watchObjectResource?.id}
          rsqlSources={[debouncedTimeEdgesFilter]}
        />
      </TabPanel>
      {isShowClearModal && (
        <ConfirmModal
          actionBtnText={t('modal.changeObjectAxisY.btn')}
          isShow={isShowClearModal}
          text={t('modal.changeObjectAxisY.warning')}
          title={t('modal.changeObjectAxisY.title')}
          containerStyle={{
            px: 2.5,
            py: 2.5,
            // borderRadius: 0
          }}
          onClose={() => handleClose(true)}
          onConfirm={handleClearOnChangeObject}
        />
      )}
    </Box>
  )
}
